import JSZip from 'jszip';
import type { PurchaseReceiptItem } from '../types/purchaseReceipt';
import { purchaseReceiptService } from './purchaseReceiptService';
import { storeService } from './storeService';
import { warehouseGoodsCatalogService } from './warehouseGoodsCatalogService';

const XML_NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const TEMPLATE_FILE_NAME = 'NhapKhauNVLNhapHang-template.xlsx';
const TEMPLATE_SHEET_NAME = 'NhapKhauNhapHang';
const DATA_START_ROW = 9;
const TEMPLATE_LAST_ROW = 1008;
const CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XML_DECLARATION = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

export const CUKCUK_TEMPLATE_URL = `/templates/cukcuk/${TEMPLATE_FILE_NAME}`;

export interface CukcukExportErrorDetail {
  receiptId?: string;
  itemId?: string;
  materialId?: string;
  materialName?: string;
  field: string;
  message: string;
}

export interface CukcukBatchSkippedReceipt {
  receiptId: string;
  errors: CukcukExportErrorDetail[];
}

export class CukcukExportValidationError extends Error {
  readonly code = 'CUKCUK_EXPORT_VALIDATION_FAILED';
  readonly errors: CukcukExportErrorDetail[];

  constructor(errors: CukcukExportErrorDetail[]) {
    super('CUKCUK export validation failed');
    this.name = 'CukcukExportValidationError';
    this.errors = errors;
  }
}

export class CukcukExportTemplateError extends Error {
  readonly code: 'CUKCUK_EXPORT_TEMPLATE_NOT_FOUND' | 'CUKCUK_EXPORT_TEMPLATE_INVALID';

  constructor(
    code: 'CUKCUK_EXPORT_TEMPLATE_NOT_FOUND' | 'CUKCUK_EXPORT_TEMPLATE_INVALID',
    message: string,
  ) {
    super(message);
    this.name = 'CukcukExportTemplateError';
    this.code = code;
  }
}

export interface CukcukExportOptions {
  templateBytes?: ArrayBuffer | Uint8Array;
  templateUrl?: string;
  fetchImpl?: typeof fetch;
}

export interface CukcukExportResult {
  blob: Blob;
  filename: string;
  contentType: string;
  supplierName?: string;
}

export interface CukcukBatchExportResult extends CukcukExportResult {
  skipped: CukcukBatchSkippedReceipt[];
}

interface TemplateSheetContext {
  zip: JSZip;
  sheetPath: string;
  sheetDocument: XMLDocument;
  sharedStringsDocument?: XMLDocument;
}

const withSingleXmlDeclaration = (xml: string): string =>
  `${XML_DECLARATION}\n${xml.replace(/^\s*(?:<\?xml[^?]*\?>\s*)+/, '')}`;

const parseXml = (xml: string, description: string): XMLDocument => {
  const document = new DOMParser().parseFromString(xml, 'application/xml');
  if (document.getElementsByTagName('parsererror').length > 0) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      `Không thể đọc ${description} của template CUKCUK`,
    );
  }
  return document;
};

const directChildren = (parent: Element, tagName: string): Element[] =>
  Array.from(parent.children).filter((child) => child.localName === tagName);

const findDirectChild = (parent: Element, tagName: string): Element | undefined =>
  directChildren(parent, tagName)[0];

const findCell = (row: Element, cellRef: string): Element | undefined =>
  directChildren(row, 'c').find((cell) => cell.getAttribute('r') === cellRef);

const rowCache = new WeakMap<XMLDocument, Map<number, Element>>();

const findRow = (sheetDocument: XMLDocument, rowNumber: number): Element | undefined =>
  (() => {
    let rowsByNumber = rowCache.get(sheetDocument);
    if (!rowsByNumber) {
      rowsByNumber = new Map(
        Array.from(sheetDocument.getElementsByTagNameNS(XML_NS, 'row')).map((row) => [
          Number(row.getAttribute('r')),
          row,
        ]),
      );
      rowCache.set(sheetDocument, rowsByNumber);
    }
    return rowsByNumber.get(rowNumber);
  })();

const getSharedString = (document: XMLDocument | undefined, index: number): string => {
  if (!document) return '';
  const items = Array.from(document.getElementsByTagName('*')).filter((element) => element.localName === 'si');
  const item = items[index];
  if (!item) return '';
  return Array.from(item.getElementsByTagName('*'))
    .filter((element) => element.localName === 't')
    .map((element) => element.textContent || '')
    .join('');
};

const getSharedStringItems = (document: XMLDocument): Element[] =>
  Array.from(document.getElementsByTagName('*')).filter((element) => element.localName === 'si');

const getOrCreateSharedStringIndex = (document: XMLDocument, value: string): number => {
  const items = getSharedStringItems(document);
  const existingIndex = items.findIndex((item) =>
    Array.from(item.getElementsByTagName('*'))
      .filter((element) => element.localName === 't')
      .map((element) => element.textContent || '')
      .join('') === value,
  );
  const sharedStringsRoot = Array.from(document.getElementsByTagName('*')).find(
    (element) => element.localName === 'sst',
  );
  if (!sharedStringsRoot) return -1;

  const count = Number(sharedStringsRoot.getAttribute('count') || 0);
  sharedStringsRoot.setAttribute('count', String(count + 1));
  if (existingIndex >= 0) return existingIndex;

  const item = document.createElementNS(XML_NS, 'si');
  const text = document.createElementNS(XML_NS, 't');
  text.setAttributeNS('http://www.w3.org/XML/1998/namespace', 'xml:space', 'preserve');
  text.textContent = value;
  item.appendChild(text);
  sharedStringsRoot.appendChild(item);
  sharedStringsRoot.setAttribute('uniqueCount', String(items.length + 1));
  return items.length;
};

const getCellText = (cell: Element | undefined, sharedStringsDocument?: XMLDocument): string => {
  if (!cell) return '';
  const type = cell.getAttribute('t');
  const value = findDirectChild(cell, 'v')?.textContent || '';
  if (type === 's') return getSharedString(sharedStringsDocument, Number(value));
  if (type === 'inlineStr') return findDirectChild(findDirectChild(cell, 'is') || cell, 't')?.textContent || '';
  return value;
};

const normalizeRelationshipTarget = (target: string): string => {
  const normalized = target.replace(/^\//, '');
  return normalized.startsWith('xl/') ? normalized : `xl/${normalized}`;
};

const findTemplateSheetPath = (workbookXml: string, relationshipsXml: string): string => {
  const workbookDocument = parseXml(workbookXml, 'workbook.xml');
  const sheet = Array.from(workbookDocument.getElementsByTagName('*')).find(
    (element) => element.localName === 'sheet' && element.getAttribute('name') === TEMPLATE_SHEET_NAME,
  );
  if (!sheet) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      `Không tìm thấy sheet ${TEMPLATE_SHEET_NAME}`,
    );
  }

  const relationshipId = sheet.getAttributeNS(REL_NS, 'id') || sheet.getAttribute('r:id');
  const relationshipsDocument = parseXml(relationshipsXml, 'workbook relationships');
  const relationship = Array.from(relationshipsDocument.getElementsByTagName('*')).find(
    (element) => element.localName === 'Relationship' && element.getAttribute('Id') === relationshipId,
  );
  const target = relationship?.getAttribute('Target');
  if (!target) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      `Không tìm thấy file worksheet của sheet ${TEMPLATE_SHEET_NAME}`,
    );
  }
  return normalizeRelationshipTarget(target);
};

const loadTemplateContext = async (
  options: CukcukExportOptions,
): Promise<TemplateSheetContext> => {
  let bytes = options.templateBytes;
  if (!bytes) {
    const fetcher = options.fetchImpl || fetch;
    let response: Response;
    try {
      response = await fetcher(options.templateUrl || CUKCUK_TEMPLATE_URL);
    } catch {
      throw new CukcukExportTemplateError(
        'CUKCUK_EXPORT_TEMPLATE_NOT_FOUND',
        'Không thể tải template CUKCUK',
      );
    }
    if (!response.ok) {
      throw new CukcukExportTemplateError(
        'CUKCUK_EXPORT_TEMPLATE_NOT_FOUND',
        'Không tìm thấy template CUKCUK',
      );
    }
    bytes = await response.arrayBuffer();
  }

  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(bytes);
  } catch {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      'Template CUKCUK không phải file XLSX hợp lệ',
    );
  }

  const workbookFile = zip.file('xl/workbook.xml');
  const relationshipsFile = zip.file('xl/_rels/workbook.xml.rels');
  if (!workbookFile || !relationshipsFile) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      'Template CUKCUK thiếu metadata workbook',
    );
  }

  const sheetPath = findTemplateSheetPath(
    await workbookFile.async('string'),
    await relationshipsFile.async('string'),
  );
  const sheetFile = zip.file(sheetPath);
  if (!sheetFile) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      `Không tìm thấy worksheet ${TEMPLATE_SHEET_NAME}`,
    );
  }

  const sheetDocument = parseXml(await sheetFile.async('string'), `${TEMPLATE_SHEET_NAME}.xml`);
  const sharedStringsFile = zip.file('xl/sharedStrings.xml');
  const sharedStringsDocument = sharedStringsFile
    ? parseXml(await sharedStringsFile.async('string'), 'sharedStrings.xml')
    : undefined;
  const headerRow = findRow(sheetDocument, 8);
  const expectedHeaders = [
    'Mã NVL (*)',
    'Nguyên vật liệu',
    'Mã Kho (*)',
    'Đơn vị tính (*)',
    'Số lượng (*)',
    'Đơn giá',
    'Thành tiền',
    'Hạn sử dụng',
    'Ghi chú',
  ];
  const actualHeaders = headerRow
    ? ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'].map((column) =>
        getCellText(findCell(headerRow, `${column}8`), sharedStringsDocument),
      )
    : [];
  if (!headerRow || actualHeaders.some((value, index) => value !== expectedHeaders[index])) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      `Header sheet ${TEMPLATE_SHEET_NAME} không đúng template CUKCUK`,
    );
  }

  return { zip, sheetPath, sheetDocument, sharedStringsDocument };
};

const clearCell = (cell: Element): void => {
  cell.removeAttribute('t');
  while (cell.firstChild) cell.removeChild(cell.firstChild);
};

const setCellValue = (
  document: XMLDocument,
  cell: Element,
  value: string | number | null,
  sharedStringsDocument?: XMLDocument,
): void => {
  clearCell(cell);
  if (value === null || value === '') return;

  if (typeof value === 'number') {
    cell.setAttribute('t', 'n');
    const valueElement = document.createElementNS(XML_NS, 'v');
    valueElement.textContent = String(value);
    cell.appendChild(valueElement);
    return;
  }

  if (sharedStringsDocument) {
    const sharedStringIndex = getOrCreateSharedStringIndex(sharedStringsDocument, value);
    if (sharedStringIndex >= 0) {
      cell.setAttribute('t', 's');
      const valueElement = document.createElementNS(XML_NS, 'v');
      valueElement.textContent = String(sharedStringIndex);
      cell.appendChild(valueElement);
      return;
    }
  }

  // Fallback for non-CUKCUK templates that do not contain sharedStrings.xml.
  cell.setAttribute('t', 'inlineStr');
  const inlineString = document.createElementNS(XML_NS, 'is');
  const text = document.createElementNS(XML_NS, 't');
  text.setAttributeNS('http://www.w3.org/XML/1998/namespace', 'xml:space', 'preserve');
  text.textContent = value;
  inlineString.appendChild(text);
  cell.appendChild(inlineString);
};

const ensureCell = (document: XMLDocument, row: Element, cellRef: string): Element => {
  const existing = findCell(row, cellRef);
  if (existing) return existing;
  const cell = document.createElementNS(XML_NS, 'c');
  cell.setAttribute('r', cellRef);
  row.appendChild(cell);
  return cell;
};

const excelDateSerial = (value: Date): number => {
  const utcDate = Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
  return (utcDate - Date.UTC(1899, 11, 30)) / 86400000;
};

const calculateAmount = (quantity: number, unitPrice: number): number => {
  const quantityScale = 10 ** ((String(quantity).split('.')[1] || '').length);
  const priceScale = 10 ** ((String(unitPrice).split('.')[1] || '').length);
  const scaledQuantity = Math.round(quantity * quantityScale);
  const scaledPrice = Math.round(unitPrice * priceScale);
  return (scaledQuantity * scaledPrice) / (quantityScale * priceScale);
};

const sanitizeFilenamePart = (value: string): string => {
  const withoutDiacritics = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
  const withoutControlCharacters = Array.from(withoutDiacritics, (character) =>
    character.charCodeAt(0) < 32 ? '_' : character,
  ).join('');
  const sanitized = withoutControlCharacters
    .trim()
    .replace(/[<>:"/\\|?*+&]/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/\.\.+/g, '_')
    .replace(/[. ]+$/g, '')
    .slice(0, 120);
  return sanitized || 'receipt';
};

const formatReceiptDateForFilename = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addError = (
  errors: CukcukExportErrorDetail[],
  detail: Omit<CukcukExportErrorDetail, 'message'> & { message: string },
): void => {
  errors.push(detail);
};

const validateAndBuildRows = async (receiptId: string) => {
  const receiptWithItems = await purchaseReceiptService.getReceiptById(receiptId);
  const errors: CukcukExportErrorDetail[] = [];
  if (!receiptWithItems) {
    addError(errors, { field: 'receipt', message: 'Không tìm thấy phiếu nhập' });
    throw new CukcukExportValidationError(errors);
  }
  if (receiptWithItems.items.length === 0) {
    addError(errors, { field: 'items', message: 'Phiếu nhập chưa có nguyên liệu' });
    throw new CukcukExportValidationError(errors);
  }
  const supplierName = receiptWithItems.receipt.supplierName?.trim() || '';
  if (!supplierName) {
    addError(errors, { field: 'supplier', message: 'Phiếu nhập chưa có nhà cung cấp' });
  }

  const [materials, store] = await Promise.all([
    warehouseGoodsCatalogService.getAll(),
    storeService.getStoreById(receiptWithItems.receipt.storeLocationId),
  ]);
  const materialsById = new Map(materials.map((material) => [material.id, material]));
  if (!store) {
    addError(errors, { field: 'branch', message: 'Không tìm thấy cơ sở của phiếu nhập' });
  }
  if (!store?.cukcukWarehouseCode) {
    addError(errors, { field: 'cukcuk_warehouse_code', message: 'Cơ sở chưa cấu hình Mã kho CUKCUK' });
  }

  const rows = receiptWithItems.items.map((item: PurchaseReceiptItem) => {
    const material = materialsById.get(item.materialId);
    const materialName = material?.name || item.materialId;
    const common = { itemId: item.id, materialId: item.materialId, materialName };
    if (!material) addError(errors, { ...common, field: 'material', message: 'Không tìm thấy nguyên liệu' });
    if (!material?.cukcukMaterialCode) {
      addError(errors, { ...common, field: 'cukcuk_material_code', message: 'Chưa cấu hình Mã NVL CUKCUK' });
    }
    if (!material?.cukcukUnit) {
      addError(errors, { ...common, field: 'cukcuk_unit', message: 'Chưa cấu hình ĐVT CUKCUK' });
    }
    if (typeof item.quantity !== 'number' || !Number.isFinite(item.quantity) || item.quantity <= 0) {
      addError(errors, { ...common, field: 'quantity', message: 'Số lượng phải lớn hơn 0' });
    }
    if (item.unitPrice !== null && item.unitPrice !== undefined) {
      if (typeof item.unitPrice !== 'number' || !Number.isFinite(item.unitPrice) || item.unitPrice < 0) {
        addError(errors, { ...common, field: 'unitPrice', message: 'Đơn giá không hợp lệ' });
      }
    }

    return {
      item,
      material,
      warehouseCode: store?.cukcukWarehouseCode || null,
    };
  });

  if (errors.length > 0) throw new CukcukExportValidationError(errors);
  return { receipt: receiptWithItems.receipt, rows, supplierName };
};

export const exportPurchaseReceiptToCukcuk = async (
  receiptId: string,
  options: CukcukExportOptions = {},
): Promise<CukcukExportResult> => {
  const { receipt, rows, supplierName } = await validateAndBuildRows(receiptId);
  if (rows.length > TEMPLATE_LAST_ROW - DATA_START_ROW + 1) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      `Phiếu nhập có quá ${TEMPLATE_LAST_ROW - DATA_START_ROW + 1} dòng dữ liệu`,
    );
  }

  const context = await loadTemplateContext(options);
  const sheetData = Array.from(context.sheetDocument.getElementsByTagName('*')).find(
    (element) => element.localName === 'sheetData',
  );
  if (!sheetData) {
    throw new CukcukExportTemplateError(
      'CUKCUK_EXPORT_TEMPLATE_INVALID',
      'Template CUKCUK thiếu sheetData',
    );
  }

  for (let rowNumber = DATA_START_ROW; rowNumber <= TEMPLATE_LAST_ROW; rowNumber += 1) {
    const row = findRow(context.sheetDocument, rowNumber);
    if (!row) continue;
    directChildren(row, 'c').forEach((cell) => {
      const cellRef = cell.getAttribute('r') || '';
      if (/^[A-I]\d+$/.test(cellRef)) clearCell(cell);
    });
  }

  rows.forEach(({ item, material, warehouseCode }, index) => {
    const rowNumber = DATA_START_ROW + index;
    const row = findRow(context.sheetDocument, rowNumber);
    if (!row || !material || !warehouseCode) return;
    const values: Record<string, string | number | null> = {
      A: material.cukcukMaterialCode || null,
      B: material.name,
      C: warehouseCode,
      D: material.cukcukUnit || null,
      E: item.quantity,
      F: item.unitPrice ?? null,
      G: item.unitPrice === null || item.unitPrice === undefined ? null : calculateAmount(item.quantity, item.unitPrice),
      H: item.expiryDate ? excelDateSerial(item.expiryDate) : null,
      I: item.note || null,
    };
    Object.entries(values).forEach(([column, value]) => {
      setCellValue(
        context.sheetDocument,
        ensureCell(context.sheetDocument, row, `${column}${rowNumber}`),
        value,
        context.sharedStringsDocument,
      );
    });
  });

  const serializer = new XMLSerializer();
  context.zip.file(context.sheetPath, withSingleXmlDeclaration(serializer.serializeToString(context.sheetDocument)));
  if (context.sharedStringsDocument) {
    context.zip.file(
      'xl/sharedStrings.xml',
      withSingleXmlDeclaration(serializer.serializeToString(context.sharedStringsDocument)),
    );
  }
  const blob = await context.zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  return {
    blob,
    filename: `CUKCUK_NhapHang_${formatReceiptDateForFilename(receipt.date)}_NCC_${sanitizeFilenamePart(supplierName)}_${sanitizeFilenamePart(receipt.code)}.xlsx`,
    contentType: CONTENT_TYPE,
    supplierName,
  };
};

export const exportPurchaseReceiptsToCukcukZip = async (
  receiptIds: string[],
  options: CukcukExportOptions = {},
): Promise<CukcukBatchExportResult> => {
  if (!receiptIds.length) throw new Error('Chưa chọn phiếu nhập để xuất');
  type BatchExportAttempt =
    | { receiptId: string; result: CukcukExportResult }
    | { receiptId: string; error: CukcukExportValidationError };
  const settled = await Promise.all(receiptIds.map(async (receiptId): Promise<BatchExportAttempt> => {
    try {
      return { receiptId, result: await exportPurchaseReceiptToCukcuk(receiptId, options) };
    } catch (error) {
      if (error instanceof CukcukExportValidationError) {
        return { receiptId, error };
      }
      throw error;
    }
  }));
  const exports = settled
    .filter((entry): entry is Extract<BatchExportAttempt, { result: CukcukExportResult }> => 'result' in entry)
    .map((entry) => entry.result);
  const skipped = settled
    .filter((entry): entry is Extract<BatchExportAttempt, { error: CukcukExportValidationError }> => 'error' in entry)
    .map((entry) => ({ receiptId: entry.receiptId, errors: entry.error.errors }));
  if (!exports.length) {
    throw new CukcukExportValidationError(
      skipped.flatMap(({ receiptId, errors }) => errors.map((error) => ({ ...error, receiptId }))),
    );
  }
  const zip = new JSZip();
  for (const result of exports) zip.file(result.filename, result.blob);
  return {
    blob: await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' }),
    filename: `CUKCUK_NhapHang_${exports.length}_phieu.zip`,
    contentType: 'application/zip',
    skipped,
    supplierName: Array.from(new Set(exports.map((result) => result.supplierName).filter(Boolean))).length === 1
      ? exports[0].supplierName
      : 'Nhiều nhà cung cấp',
  };
};
